import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

// Announces the page title to screen readers after each route change (SPA navigation is otherwise silent).
@Injectable({
    providedIn: 'root'
})
export class RouteAnnouncerService {
    private readonly router = inject(Router);
    private readonly title = inject(Title);
    private readonly liveAnnouncer = inject(LiveAnnouncer);
    private readonly destroyRef = inject(DestroyRef);

    private started = false;
    private lastPath: string | null = null;

    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
            .subscribe(event => this.announce(event));
    }

    private announce(event: NavigationEnd): void {
        const path = event.urlAfterRedirects.split(/[?#]/)[0];
        const isFirstNavigation = this.lastPath === null;
        const samePath = path === this.lastPath;
        this.lastPath = path;

        // The initial load is announced by the browser; query-param-only changes keep the same page.
        if (isFirstNavigation || samePath) {
            return;
        }

        const pageTitle = this.title.getTitle().trim();
        if (pageTitle) {
            this.liveAnnouncer.announce(pageTitle, 'polite');
        }
    }
}
